import {
  CountrySelectFormField,
  Form,
  FormSubmitButton,
  PhoneNumberFormField,
  PlacesAutocompleteFormField,
  StateSelectFormField,
  TextFormField,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import {
  getFirmOfficesQueryOptions,
  useCreateFirmOffice,
  useUpdateFirmOffice,
} from "@/lib/data/react-query";
import { FirmOfficeSchema } from "@/lib/data/schemas/firm/FirmSchema";
import { type FirmOffice } from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { toLower } from "lodash";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { type z } from "zod";

const FORM_ID = "add-or-edit-office-form";

interface AddOrEditOfficeModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  setActiveOfficeId: (id: string) => void;
  editing: boolean;
}

type ConditionalProps =
  | {
      editing: true;
      activeOffice: FirmOffice;
    }
  | {
      editing: false;
      activeOffice?: never;
    };

const formSchema = FirmOfficeSchema.pick({
  label: true,
  phoneNumber: true,
  street1: true,
  street2: true,
  city: true,
  state: true,
  zipCode: true,
  country: true,
});

const defaultObject = {
  label: "",
  phoneNumber: "",
  street1: "",
  street2: "",
  city: "",
  state: "",
  zipCode: "",
  country: "United States",
};

export function AddOrEditOfficeModal({
  open,
  setOpen,
  activeOffice,
  setActiveOfficeId,
  editing,
}: AddOrEditOfficeModalProps & ConditionalProps) {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: editing ? { ...defaultObject, ...activeOffice } : defaultObject,
    mode: "onSubmit",
  });

  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open, form]);

  const { data: officesData } = useQuery(getFirmOfficesQueryOptions());
  const offices = useMemo(() => officesData?.data ?? [], [officesData]);

  const createFirmOffice = useCreateFirmOffice();
  const updateFirmOffice = useUpdateFirmOffice();
  const isFormLoading =
    createFirmOffice.isPending || updateFirmOffice.isPending;

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    if (
      offices.some(
        (office) =>
          toLower(office.label) === toLower(data.label) &&
          office.id !== activeOffice?.id,
      )
    ) {
      form.setError("label", {
        message: "Office label must be unique",
      });
      return;
    }
    if (editing) {
      await updateFirmOffice.mutateAsync({
        id: activeOffice.id,
        dateModified: activeOffice.dateModified,
        ...data,
      });
    } else {
      setActiveOfficeId("temp");
      await createFirmOffice
        .mutateAsync({
          ...data,
        })
        .then((res) => {
          setActiveOfficeId(res.data.id);
          form.reset();
        });
    }
    setOpen(false);
  };

  return (
    <Form {...form}>
      <AppModal
        open={open}
        onOpenChange={setOpen}
        isDirty={form.formState.isDirty}
        title={editing ? "Update office" : "Create office"}
        description={
          editing
            ? "Update the office details below"
            : "Create a new office to your firm"
        }
        loading={isFormLoading}
        size="2xl"
        footer={
          <div className="flex w-full flex-wrap items-center justify-end gap-2">
            <FormCancelButton
              setOpen={setOpen}
              isDirty={form.formState.isDirty}
            />
            <FormSubmitButton
              submitFormId={FORM_ID}
              loading={isFormLoading}
              disabled={isFormLoading}
            />
          </div>
        }
      >
        <form
          id={FORM_ID}
          className="flex min-h-0 grow flex-col overflow-hidden"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="my-4 grid grid-cols-1 gap-4 overflow-y-auto p-6 sm:grid-cols-2">
            <TextFormField
              register={form.register("label")}
              label="Label*"
              dataTestId="firm-office-label"
            />
            <PhoneNumberFormField
              register={form.register("phoneNumber")}
              label="Phone number*"
              dataTestId="firm-office-phone-number"
            />
            <PlacesAutocompleteFormField
              register={form.register("street1")}
              defaultValue={form.getValues("street1")}
              dataTestId="firm-office-address-1"
              label="Address 1*"
              onAddressSelect={(address) => {
                form.setValue("city", address.city, {
                  shouldValidate: true,
                });
                form.setValue("state", address.state, {
                  shouldValidate: true,
                });
                form.setValue("zipCode", address.zipCode, {
                  shouldValidate: true,
                });
                form.setValue("street1", address.streetAddress, {
                  shouldValidate: true,
                });
                form.setValue("country", address.country, {
                  shouldValidate: true,
                });

                form.setValue("street2", "");
              }}
            />
            <TextFormField
              register={form.register("street2")}
              label="Address 2"
            />
            <TextFormField
              register={form.register("zipCode")}
              label="Zip code*"
              dataTestId="firm-office-zip-code"
            />
            <TextFormField
              register={form.register("city")}
              label="City*"
              dataTestId="firm-office-city"
            />

            <StateSelectFormField
              register={form.register("state")}
              label="State*"
              name="state"
              country={form.watch("country")}
            />
            <CountrySelectFormField
              register={form.register("country")}
              label="Country*"
              name="country"
              stateSelectName="state"
            />
          </div>
        </form>
      </AppModal>
    </Form>
  );
}
